/**
 * useScheduledBatches — E-93 Story 93.6.
 *
 * Lists ütemezett bulk-email batch-eket (PENDING+QUEUED by default) and
 * exposes a `cancel(batchId)` action that hits the backend cancel
 * endpoint and refreshes the list. Pure useState/useEffect to match the
 * codebase pattern (no TanStack Query in kgc-web).
 *
 * Race-safe behaviour:
 * - The cancel action does NOT optimistically remove from the list.
 *   We refetch after the server confirms `cancelled` (or 409
 *   `not_cancellable`). Optimistic removal would mask the dispatcher
 *   race where a click lands the same millisecond a delayed job fires.
 * - Polling is opt-in via `pollMs`. When the page is hidden the
 *   `setInterval` keeps firing — that is intentional, the list lengths
 *   are tiny and a stale "ütemezett" badge after a tab switch is the
 *   worse UX.
 */

import {
  cancelScheduledBatch,
  fetchScheduledBatches,
  type BulkEmailBatchStatus,
  type CancelOutcome,
  type ScheduledBatchRow,
} from '@/api/bulk-email';
import { useCallback, useEffect, useState } from 'react';

interface UseScheduledBatchesOptions {
  statuses?: BulkEmailBatchStatus[];
  limit?: number;
  /**
   * Re-fetch interval in milliseconds. Off by default. Set to e.g.
   * 30000 for the BulkEmailPage list panel; the franchise manager wants
   * to see the dispatch transitions without manual refresh.
   */
  pollMs?: number;
}

interface CancelResult {
  outcome: CancelOutcome;
  status: string;
}

interface UseScheduledBatchesResult {
  data: ScheduledBatchRow[];
  totalCount: number;
  isLoading: boolean;
  isFetching: boolean;
  error: string | null;
  refetch: () => Promise<void>;
  /**
   * Cancel a batch + refresh the list. Returns the cancel outcome so
   * the caller can toast the right message (cancelled vs already_cancelled
   * vs 409 race-loser). Throws on network/server errors.
   */
  cancel: (batchId: string) => Promise<CancelResult>;
  /** True while a specific batchId's cancel is in flight (for spinner UX). */
  isCancellingId: string | null;
}

export function useScheduledBatches(
  options: UseScheduledBatchesOptions = {}
): UseScheduledBatchesResult {
  const [data, setData] = useState<ScheduledBatchRow[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [isFetching, setIsFetching] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isCancellingId, setIsCancellingId] = useState<string | null>(null);

  const { statuses, limit, pollMs } = options;
  const statusesKey = statuses ? statuses.join(',') : '';

  const refetch = useCallback(async (): Promise<void> => {
    setIsFetching(true);
    setError(null);
    try {
      const fetchOptions: Parameters<typeof fetchScheduledBatches>[0] = {};
      if (statuses && statuses.length > 0) fetchOptions.statuses = statuses;
      if (limit !== undefined) fetchOptions.limit = limit;
      const res = await fetchScheduledBatches(fetchOptions);
      setData(res.data);
      setTotalCount(res.totalCount);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setIsLoading(false);
      setIsFetching(false);
    }
    // statusesKey is the stable array identity proxy
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusesKey, limit]);

  useEffect(() => {
    void refetch();
    if (!pollMs || pollMs <= 0) return;
    const id = setInterval(() => void refetch(), pollMs);
    return () => clearInterval(id);
  }, [refetch, pollMs]);

  const cancel = useCallback(
    async (batchId: string): Promise<CancelResult> => {
      setIsCancellingId(batchId);
      try {
        const result = await cancelScheduledBatch(batchId);
        await refetch();
        return { outcome: result.outcome, status: result.status };
      } finally {
        setIsCancellingId(null);
      }
    },
    [refetch]
  );

  return {
    data,
    totalCount,
    isLoading,
    isFetching,
    error,
    refetch,
    cancel,
    isCancellingId,
  };
}
